import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import './SeatingChart.css';

/* ─── helpers ─────────────────────────────────────────────────── */

function buildGuestList(rsvps) {
  const guests = [];
  rsvps
    .filter((r) => r.attending)
    .forEach((r) => {
      guests.push({ id: `${r.name}-main`, label: r.name });
      const raw = r.guestsNames?.[0] ?? '';
      const extras = raw ? parseInt(raw.replace('+', ''), 10) : 0;
      for (let i = 1; i <= extras; i++) {
        guests.push({
          id: `${r.name}-extra-${i}`,
          label: `+1 de ${r.name}`,
        });
      }
    });
  guests.sort((a, b) => a.label.localeCompare(b.label));
  return guests;
}

const PALETTE = {
  bg: '#fafaf8',
  room: '#ffffff',
  roomBorder: '#c8bfb0',
  tableCircle: '#ffffff',
  tableStroke: '#6b5e4e',
  chairEmpty: '#e8e2da',
  chairStroke: '#9e8f82',
  chairAssigned: '#4a90d9',
  chairAssignedStroke: '#2c6fad',
  labelColor: '#3a3028',
  tableLabel: '#6b5e4e',
  danceFloorBg: '#f0ece6',
  danceFloorBorder: '#d8d0c8',
};

/* ─── geometry helpers ────────────────────────────────────────── */
function chairPositions(cx, cy, tableR, seatCount) {
  const chairR = tableR * 0.32;
  const orbitR = tableR + chairR + 4;
  const positions = [];
  for (let i = 0; i < seatCount; i++) {
    const angle = (2 * Math.PI * i) / seatCount - Math.PI / 2;
    positions.push({
      x: cx + orbitR * Math.cos(angle),
      y: cy + orbitR * Math.sin(angle),
      angle,
      r: chairR,
    });
  }
  return positions;
}

/* ─── main component ──────────────────────────────────────────── */
export default function SeatingChart({ rsvps }) {
  const API_URL = window.location.hostname === 'localhost' ? "http://localhost:5001" : "https://esponjoboda.com";
  
  /* Attempt to load prior configuration from localStorage */
  const storedConfig = (() => {
    try { return JSON.parse(localStorage.getItem('seating_config')) || {}; } catch { return {}; }
  })();

  /* config */
  const [danceFloorAlign, setDanceFloorAlign] = useState(storedConfig.danceFloorAlign ?? 'center'); // 'top', 'center', 'bottom', 'none'
  const [showGroomTable, setShowGroomTable] = useState(storedConfig.showGroomTable ?? true);
  const [showDrinksBar, setShowDrinksBar] = useState(storedConfig.showDrinksBar ?? true);

  const [numTables, setNumTables] = useState(storedConfig.numTables ?? 8);
  const [seatsPerTable, setSeatsPerTable] = useState(storedConfig.seatsPerTable ?? 8);

  /* editable guest name overrides — stored in localStorage, separate from RSVP data */
  const [guestNameOverrides, setGuestNameOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem('seatingNameOverrides') || '{}'); }
    catch { return {}; }
  });
  const [editingGuestId, setEditingGuestId] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  /* state */
  const allGuests = buildGuestList(rsvps).map((g) => ({
    ...g,
    label: guestNameOverrides[g.id] ?? g.label,
  }));
  const [assignments, setAssignments] = useState(() => {
    try { return JSON.parse(localStorage.getItem('seating_assignments')) || {}; } catch { return {}; }
  });
  const [tooltip, setTooltip] = useState(null);
  const [hoveredSeat, setHoveredSeat] = useState(null);

  // Load from Cloudflare KV backend on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    fetch(`${API_URL}/api/seating`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(res => {
        if (res.ok && res.data && Object.keys(res.data).length > 0) {
          const { config, nameOverrides, savedAssignments } = res.data;
          if (config) {
            setDanceFloorAlign(config.danceFloorAlign ?? 'center');
            setShowGroomTable(config.showGroomTable ?? true);
            setShowDrinksBar(config.showDrinksBar ?? true);
            setNumTables(config.numTables ?? 8);
            setSeatsPerTable(config.seatsPerTable ?? 8);
          }
          if (nameOverrides) setGuestNameOverrides(nameOverrides);
          if (savedAssignments) setAssignments(savedAssignments);
        }
      })
      .catch(err => console.error("Error fetching cloud seating chart:", err));
  }, [API_URL]);

  const saveToCloud = () => {
    const token = localStorage.getItem('adminToken');
    const payload = {
      config: {
        danceFloorAlign, showGroomTable, showDrinksBar, numTables, seatsPerTable
      },
      nameOverrides: guestNameOverrides,
      savedAssignments: assignments
    };
    
    fetch(`${API_URL}/api/seating`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    })
      .then(res => res.json())
      .then(res => {
        if (res.ok) alert("Configuración guardada en la nube exitosamente.");
        else alert("Error al guardar en la nube: " + (res.error || "Desconocido"));
      })
      .catch(err => {
        console.error("Error saving to cloud:", err);
        alert("Error al guardar en la nube.");
      });
  };

  const [selectedGuestId, setSelectedGuestId] = useState(null);
  const [expandedTables, setExpandedTables] = useState({});

  const toggleTable = (tIdx) => {
    setExpandedTables(prev => ({ ...prev, [tIdx]: !prev[tIdx] }));
  };

  const groupedAssignments = useMemo(() => {
    const groups = {};
    for (const [seatKey, guestId] of Object.entries(assignments)) {
      const [tIdx, sIdx] = seatKey.split('-');
      if (!groups[tIdx]) groups[tIdx] = [];
      groups[tIdx].push({ seatKey, guestId, sIdx: +sIdx });
    }
    // Sort seats inside each table
    Object.values(groups).forEach(seats => seats.sort((a, b) => a.sIdx - b.sIdx));
    return groups;
  }, [assignments]);

  const canvasRef = useRef(null);
  const seatsRef = useRef([]);

  const assignedGuestIds = useMemo(() => new Set(Object.values(assignments)), [assignments]);
  const unassignedGuests = useMemo(() => allGuests.filter((g) => !assignedGuestIds.has(g.id)), [allGuests, assignedGuestIds]);

  // If the currently selected guest is assigned somewhere else or doesn't exist, clear selection
  useEffect(() => {
    if (selectedGuestId && assignedGuestIds.has(selectedGuestId)) {
      setSelectedGuestId(null);
    }
  }, [assignedGuestIds, selectedGuestId]);

  /* persist name overrides to localStorage whenever they change */
  const saveNameOverride = (guestId, newName) => {
    setGuestNameOverrides((prev) => {
      const next = { ...prev, [guestId]: newName };
      try { localStorage.setItem('seatingNameOverrides', JSON.stringify(next)); } catch { }
      return next;
    });
  };

  const startEditingGuest = (guest) => {
    setEditingGuestId(guest.id);
    setEditingValue(guest.label);
  };

  const commitEdit = () => {
    if (editingGuestId && editingValue.trim()) {
      saveNameOverride(editingGuestId, editingValue.trim());
    }
    setEditingGuestId(null);
    setEditingValue('');
  };

  /* ── Draw ────────────────────────────────────────────────── */
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    /* room padding */
    const pad = 24;
    const roomX = pad;
    const roomY = pad;
    const roomW = W - pad * 2;
    const roomH = H - pad * 2;

    /* background */
    ctx.fillStyle = PALETTE.bg;
    ctx.fillRect(0, 0, W, H);

    /* room */
    ctx.fillStyle = PALETTE.room;
    ctx.strokeStyle = PALETTE.roomBorder;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.rect(roomX, roomY, roomW, roomH);
    ctx.fill();
    ctx.stroke();

    /* 
     * ── DYNAMIC GRID COMPUTATION ────────────────────────────────────
     * Based on the canvas's physical dimensions (ratio = W / H), 
     * we determine the ideal number of rows (targetRows) and columns (targetCols).
     * We continuously increment the assumed "area" (number of required cells) 
     * until the resulting grid (targetRows * targetCols) can comfortably fit 
     * all the tables and special zones without overlapping.
     */
    // Fixed aspect ratio: use canvas actual dimensions
    const ratio = W / H;

    let targetCols = 2;
    let targetRows = 2;
    let grid = [];
    let danceCells = [];
    let groomCell = null;
    let drinksCell = null;
    let tableCells = [];

    let requiredCells = numTables;
    if (danceFloorAlign !== 'none') requiredCells += 1;
    if (showGroomTable) requiredCells += 1;
    if (showDrinksBar) requiredCells += 1;

    const getAdjacentEmpty = (cells, rows, cols, g) => {
      for (let cell of cells) {
        const neighbors = [
          { r: cell.r - 1, c: cell.c }, { r: cell.r + 1, c: cell.c },
          { r: cell.r, c: cell.c - 1 }, { r: cell.r, c: cell.c + 1 }
        ];
        for (let n of neighbors) {
          if (n.r >= 0 && n.r < rows && n.c >= 0 && n.c < cols && g[n.r][n.c] === null) {
            return n;
          }
        }
      }
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (g[r][c] === null) return { r, c };
        }
      }
      return null;
    };

    let area = requiredCells;
    while (true) {
      targetRows = Math.max(2, Math.ceil(Math.sqrt(area / ratio)));
      targetCols = Math.max(2, Math.ceil(targetRows * ratio));

      grid = [];
      for (let r = 0; r < targetRows; r++) {
        grid.push(Array(targetCols).fill(null));
      }

      danceCells = [];
      groomCell = null;
      drinksCell = null;
      tableCells = [];

      if (danceFloorAlign !== 'none') {
        /*
         * Dance floor is given exactly 1 cell to make testing its positional 
         * difference (top/center/bottom) very clear.
         */
        // Dance floor = exactly 1 cell, so position changes are visible
        const dfCol = Math.floor(targetCols / 2);
        let dfRow = Math.floor(targetRows / 2);
        if (danceFloorAlign === 'top') dfRow = 0;
        else if (danceFloorAlign === 'bottom') dfRow = targetRows - 1;

        if (dfRow < targetRows && dfCol < targetCols) {
          grid[dfRow][dfCol] = 'dance';
          danceCells.push({ r: dfRow, c: dfCol });
        }
      }

      if (showGroomTable) {
        let c = getAdjacentEmpty(danceCells.length ? danceCells : [{ r: Math.floor(targetRows / 2), c: Math.floor(targetCols / 2) }], targetRows, targetCols, grid);
        if (c) {
          grid[c.r][c.c] = 'groom';
          groomCell = c;
        }
      }

      if (showDrinksBar) {
        let c = getAdjacentEmpty(danceCells.length ? danceCells : [{ r: Math.floor(targetRows / 2), c: Math.floor(targetCols / 2) }], targetRows, targetCols, grid);
        if (c) {
          grid[c.r][c.c] = 'drinks';
          drinksCell = c;
        }
      }

      let tCount = 0;
      for (let r = 0; r < targetRows; r++) {
        for (let c = 0; c < targetCols; c++) {
          if (grid[r][c] === null && tCount < numTables) {
            grid[r][c] = `table-${tCount}`;
            tableCells.push({ r, c, index: tCount });
            tCount++;
          }
        }
      }

      if (tCount >= numTables) break;
      area += 2;
    }

    const cellW = roomW / targetCols;
    const cellH = roomH / targetRows;

    ctx.strokeStyle = 'rgba(180,170,158,0.18)';
    ctx.lineWidth = 1;
    for (let c = 1; c < targetCols; c++) {
      ctx.beginPath(); ctx.moveTo(roomX + c * cellW, roomY); ctx.lineTo(roomX + c * cellW, roomY + roomH); ctx.stroke();
    }
    for (let r = 1; r < targetRows; r++) {
      ctx.beginPath(); ctx.moveTo(roomX, roomY + r * cellH); ctx.lineTo(roomX + roomW, roomY + r * cellH); ctx.stroke();
    }

    /* compute table positions — distribute across the grid */
    const seatGeometry = [];
    const tableR = Math.min(cellW, cellH) * 0.28;

    const drawChair = (ch, key, isHovered, assignedId) => {
      const w = ch.r * 2;
      const h = ch.r * 2;

      ctx.save();
      ctx.translate(ch.x, ch.y);
      ctx.rotate(ch.angle + Math.PI / 2);

      /* chair rectangle */
      const fillColor = assignedId
        ? isHovered ? '#6aaee8' : PALETTE.chairAssigned
        : isHovered ? '#d8d0c8' : PALETTE.chairEmpty;

      ctx.fillStyle = fillColor;
      ctx.strokeStyle = assignedId ? PALETTE.chairAssignedStroke : PALETTE.chairStroke;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      const rr = 3;
      ctx.moveTo(-w / 2 + rr, -h / 2);
      ctx.lineTo(w / 2 - rr, -h / 2);
      ctx.arcTo(w / 2, -h / 2, w / 2, -h / 2 + rr, rr);
      ctx.lineTo(w / 2, h / 2 - rr);
      ctx.arcTo(w / 2, h / 2, w / 2 - rr, h / 2, rr);
      ctx.lineTo(-w / 2 + rr, h / 2);
      ctx.arcTo(-w / 2, h / 2, -w / 2, h / 2 - rr, rr);
      ctx.lineTo(-w / 2, -h / 2 + rr);
      ctx.arcTo(-w / 2, -h / 2, -w / 2 + rr, -h / 2, rr);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      seatGeometry.push({ key, cx: ch.x, cy: ch.y, r: ch.r });

      /* Name labels are intentionally hidden from the map for a cleaner look. Tooltips are used instead. */
    };

    if (danceCells.length > 0) {
      let minR = Math.min(...danceCells.map(c => c.r));
      let maxR = Math.max(...danceCells.map(c => c.r));
      let minC = Math.min(...danceCells.map(c => c.c));
      let maxC = Math.max(...danceCells.map(c => c.c));

      const dX = roomX + minC * cellW + 10;
      const dY = roomY + minR * cellH + 10;
      const dW = (maxC - minC + 1) * cellW - 20;
      const dH = (maxR - minR + 1) * cellH - 20;

      ctx.fillStyle = PALETTE.danceFloorBg;
      ctx.strokeStyle = PALETTE.danceFloorBorder;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.rect(dX, dY, dW, dH);
      ctx.fill();
      ctx.stroke();

      for (let i = 0; i < dW; i += 40) {
        ctx.beginPath();
        ctx.moveTo(dX + i, dY);
        ctx.lineTo(dX + i, dY + dH);
        ctx.strokeStyle = 'rgba(216, 208, 200, 0.4)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      for (let j = 0; j < dH; j += 40) {
        ctx.beginPath();
        ctx.moveTo(dX, dY + j);
        ctx.lineTo(dX + dW, dY + j);
        ctx.stroke();
      }

      ctx.fillStyle = PALETTE.tableLabel;
      ctx.font = `bold ${Math.max(10, tableR * 0.4)}px Garamond, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`Pista de Baile`, dX + dW / 2, dY + dH / 2);
    }

    if (showDrinksBar && drinksCell) {
      const cx = roomX + drinksCell.c * cellW + cellW / 2;
      const cy = roomY + drinksCell.r * cellH + cellH / 2;
      const bw = cellW * 0.8;
      const bh = cellH * 0.4;

      ctx.fillStyle = '#e8dccc';
      ctx.strokeStyle = '#a4917a';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.rect(cx - bw / 2, cy - bh / 2, bw, bh);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = PALETTE.tableLabel;
      ctx.font = `bold ${Math.max(10, tableR * 0.4)}px Garamond, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`Barra de Bebidas`, cx, cy);
    }

    if (showGroomTable && groomCell) {
      const cx = roomX + groomCell.c * cellW + cellW / 2;
      const cy = roomY + groomCell.r * cellH + cellH / 2;
      const gw = cellW * 0.9;
      const gh = cellH * 0.35;

      ctx.fillStyle = PALETTE.tableCircle;
      ctx.strokeStyle = PALETTE.tableStroke;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.rect(cx - gw / 2, cy - gh / 2, gw, gh);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = PALETTE.tableLabel;
      ctx.font = `bold ${Math.max(11, tableR * 0.45)}px Garamond, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`Mesa de Novios`, cx, cy);

      const chairSpaceW = gw * 0.9;

      // Draw 2 static, non-editable chairs for the groom and bride
      const chairPositions = [
        { x: cx - chairSpaceW / 4, y: cy - gh / 2 - 12, angle: -Math.PI / 2, r: tableR * 0.3 },
        { x: cx + chairSpaceW / 4, y: cy - gh / 2 - 12, angle: -Math.PI / 2, r: tableR * 0.3 }
      ];

      chairPositions.forEach((ch) => {
        // Draw static blue chairs (already assigned)
        ctx.save();
        ctx.translate(ch.x, ch.y);
        ctx.rotate(ch.angle + Math.PI / 2);
        ctx.fillStyle = PALETTE.chairAssigned;
        ctx.strokeStyle = PALETTE.chairAssignedStroke;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        const w = ch.r * 2, h = ch.r * 2, rr = 3;
        ctx.moveTo(-w / 2 + rr, -h / 2);
        ctx.lineTo(w / 2 - rr, -h / 2);
        ctx.arcTo(w / 2, -h / 2, w / 2, -h / 2 + rr, rr);
        ctx.lineTo(w / 2, h / 2 - rr);
        ctx.arcTo(w / 2, h / 2, w / 2 - rr, h / 2, rr);
        ctx.lineTo(-w / 2 + rr, h / 2);
        ctx.arcTo(-w / 2, h / 2, -w / 2, h / 2 - rr, rr);
        ctx.lineTo(-w / 2, -h / 2 + rr);
        ctx.arcTo(-w / 2, -h / 2, -w / 2 + rr, -h / 2, rr);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      });
    }

    tableCells.forEach(({ r, c, index: t }) => {
      const cx = roomX + c * cellW + cellW / 2;
      const cy = roomY + r * cellH + cellH / 2;

      ctx.beginPath();
      ctx.arc(cx, cy, tableR, 0, Math.PI * 2);
      ctx.fillStyle = PALETTE.tableCircle;
      ctx.fill();
      ctx.strokeStyle = PALETTE.tableStroke;
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = PALETTE.tableLabel;
      ctx.font = `bold ${Math.max(11, tableR * 0.55)}px Garamond, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`Mesa ${t + 1}`, cx, cy);

      const chairs = chairPositions(cx, cy, tableR, seatsPerTable,);
      chairs.forEach((ch, sIdx) => {
        const key = `${t}-${sIdx}`;
        drawChair(ch, key, hoveredSeat === key, assignments[key]);
      });
    });

    seatsRef.current = seatGeometry;
  }, [danceFloorAlign, showGroomTable, showDrinksBar, numTables, seatsPerTable, assignments, hoveredSeat]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    const ro = new ResizeObserver(() => {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
      draw();
    });
    ro.observe(container);
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    draw();
    return () => ro.disconnect();
  }, [draw]);

  useEffect(() => { draw(); }, [draw]);

  /* 
   * ── Hit testing (Canvas Interaction) ──────────────────────────
   * Because Canvas is just a flat image, there are no DOM elements for chairs.
   * We use the Pythagorean theorem (a² + b³ = c²) to calculate the distance 
   * between the mouse coordinates (x, y) and each chair's center (s.cx, s.cy).
   * If the distance is less than the chair's radius (+4px buffer), the user 
   * clicked or hovered over that specific chair.
   */
  function getSeatAtPoint(x, y) {
    for (const s of seatsRef.current) {
      const dx = x - s.cx;
      const dy = y - s.cy;
      if (Math.sqrt(dx * dx + dy * dy) < s.r + 4) return s.key;
    }
    return null;
  }

  function handleCanvasClick(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const key = getSeatAtPoint(x, y);
    if (!key) return;

    setAssignments((prev) => {
      const next = { ...prev };
      if (next[key]) {
        /* unassign if already occupied */
        delete next[key];
      } else {
        /* assign the selected guest or the first unassigned guest from our sorted list */
        const nextGuest = selectedGuestId
          ? unassignedGuests.find((g) => g.id === selectedGuestId)
          : unassignedGuests[0];
        if (nextGuest) next[key] = nextGuest.id;
      }
      return next;
    });
  }

  function handleCanvasMouseMove(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const key = getSeatAtPoint(x, y);
    setHoveredSeat(key || null);

    if (key) {
      const assignedId = assignments[key];
      if (assignedId) {
        const guest = allGuests.find((g) => g.id === assignedId);
        setTooltip({ x: e.clientX, y: e.clientY, text: guest?.label ?? '' });
      } else {
        const next = selectedGuestId
          ? unassignedGuests.find((g) => g.id === selectedGuestId)
          : unassignedGuests[0];
        setTooltip({ x: e.clientX, y: e.clientY, text: next ? `Asignar: ${next.label}` : 'Sin invitados pendientes' });
      }
    } else {
      setTooltip(null);
    }
  }

  function handleCanvasMouseLeave() {
    setHoveredSeat(null);
    setTooltip(null);
  }

  /* ── Remove guest from seat via sidebar ──────────────────── */
  function unassignGuest(guestId) {
    setAssignments((prev) => {
      const next = { ...prev };
      for (const [seat, id] of Object.entries(next)) {
        if (id === guestId) delete next[seat];
      }
      return next;
    });
  }

  /* ── Assign guest to next available seat ─────────────────── */
  function assignGuestToNextSeat(guestId) {
    setAssignments((prev) => {
      const occupiedSeats = new Set(Object.keys(prev));


      for (let t = 0; t < numTables; t++) {
        for (let s = 0; s < seatsPerTable; s++) {
          const key = `${t}-${s}`;
          if (!occupiedSeats.has(key)) {
            return { ...prev, [key]: guestId };
          }
        }
      }
      return prev;
    });
  }

  /* ── Clear all ───────────────────────────────────────────── */
  function clearAll() {
    setAssignments({});
  }

  /* ── Print ───────────────────────────────────────────────── */
  function handlePrint() {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Mapa de Mesas</title><style>
        body { margin: 0; display: flex; flex-direction: column; align-items: center; font-family: Garamond, serif; }
        img { max-width: 100%; }
        @media print { body { margin: 0; } }
      </style></head><body>
        <h2>Mapa de Mesas — Esponjoboda</h2>
        <img src="${dataUrl}" />
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
  }

  let totalAvailableSeats = numTables * seatsPerTable;
  const assignedCount = Object.keys(assignments).length;

  return (
    <div className="seating-root">
      {/* ── Controls sidebar ── */}
      <aside className="seating-sidebar">
        <h3 className="sidebar-title">Configuración</h3>

        <div className="control-group">
          <label>Pista de Baile
            <select value={danceFloorAlign} onChange={(e) => setDanceFloorAlign(e.target.value)}>
              <option value="center">Al centro</option>
              <option value="top">Arriba</option>
              <option value="bottom">Abajo</option>
            </select>
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={showGroomTable} onChange={(e) => setShowGroomTable(e.target.checked)} />
            Mesa de Novios
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={showDrinksBar} onChange={(e) => setShowDrinksBar(e.target.checked)} />
            Barra de Bebidas
          </label>

          <label>Número de mesas
            <input type="number" min="1" max="50" value={numTables} onChange={(e) => setNumTables(Math.max(1, +e.target.value))} />
          </label>
          <label>Personas por mesa
            <input type="number" min="1" max="12" value={seatsPerTable} onChange={(e) => setSeatsPerTable(Math.max(1, +e.target.value))} />
          </label>
        </div>

        <div className="seating-stats">
          <span>Invitados: <strong>{allGuests.length}</strong></span>
          <span>Asientos: <strong>{totalAvailableSeats}</strong></span>
          <span>Asignados: <strong>{assignedCount}</strong></span>
          <span>Sin asignar: <strong>{unassignedGuests.length}</strong></span>
        </div>

        <div className="sidebar-actions">
          <button className="action-save" onClick={saveToCloud} style={{ background: '#4a90d9', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>☁️ Guardar en la nube</button>
          <button className="action-clear" onClick={clearAll}>🗑 Limpiar</button>
          <button className="action-print" onClick={handlePrint}>🖨 Imprimir</button>
        </div>

        <h4 className="guest-list-title">Sin asignar ({unassignedGuests.length})</h4>
        <ul className="guest-list">
          {unassignedGuests.length === 0 && (
            <li className="guest-item guest-all-done">✔ Todos asignados</li>
          )}
          {unassignedGuests.map((g) => (
            <li
              key={g.id}
              className={`guest-item ${selectedGuestId === g.id ? 'selected' : ''}`}
              onClick={() => setSelectedGuestId(g.id)}
            >
              {editingGuestId === g.id ? (
                <input
                  className="guest-name-input"
                  autoFocus
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingGuestId(null); }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="guest-name" title="Doble clic para editar" onDoubleClick={(e) => { e.stopPropagation(); startEditingGuest(g); }}>{g.label}</span>
              )}
              <button className="guest-edit-btn" title="Editar nombre" onClick={(e) => { e.stopPropagation(); startEditingGuest(g); }}>✏</button>
              <button className="guest-assign-btn" title="Asignar al siguiente asiento libre" onClick={(e) => { e.stopPropagation(); assignGuestToNextSeat(g.id); }}>+</button>
            </li>
          ))}
        </ul>

        {assignedCount > 0 && (
          <div className="assigned-section">
            <h4 className="guest-list-title assigned-title">Asignados ({assignedCount})</h4>
            <div className="assigned-groups">
              {Object.keys(groupedAssignments).sort((a, b) => +a - +b).map(tIdx => (
                <div key={tIdx} className="assigned-table-group">
                  <div 
                    className="assigned-table-header" 
                    onClick={() => toggleTable(tIdx)}
                    style={{ cursor: 'pointer', padding: '8px', background: '#e8e2da', fontWeight: 'bold', borderRadius: '4px', marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>Mesa {+tIdx + 1} ({groupedAssignments[tIdx].length})</span>
                    <span>{expandedTables[tIdx] ? '▲' : '▼'}</span>
                  </div>
                  {expandedTables[tIdx] && (
                    <ul className="guest-list assigned-list" style={{ marginLeft: '10px' }}>
                      {groupedAssignments[tIdx].map(({ seatKey, guestId, sIdx }) => {
                        const badgeText = `Asiento ${sIdx + 1}`;
                        const guest = allGuests.find((g) => g.id === guestId);
                        return (
                          <li key={seatKey} className="guest-item assigned-item">
                            <span className="seat-badge">{badgeText}</span>
                            {editingGuestId === guestId ? (
                              <input
                                className="guest-name-input"
                                autoFocus
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={commitEdit}
                                onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingGuestId(null); }}
                              />
                            ) : (
                              <span className="guest-name" title="Doble clic para editar" onDoubleClick={() => guest && startEditingGuest(guest)}>{guest?.label}</span>
                            )}
                            <button className="guest-edit-btn" title="Editar nombre" onClick={() => guest && startEditingGuest(guest)}>✏</button>
                            <button className="guest-remove-btn" title="Quitar asignación" onClick={() => unassignGuest(guestId)}>×</button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </aside>

      {/* ── Canvas area ── */}
      <div className="seating-canvas-wrap">
        <canvas ref={canvasRef} className="seating-canvas" onClick={handleCanvasClick} onMouseMove={handleCanvasMouseMove} onMouseLeave={handleCanvasMouseLeave} />
        <p className="canvas-hint">Haz clic en un asiento para asignar • ✏ o doble clic en nombre para editar</p>
      </div>

      {tooltip && (
        <div className="seat-tooltip" style={{ left: tooltip.x + 14, top: tooltip.y - 10 }}>
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
